import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Bike, Store } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useCart } from "@/context/CartContext";
import { api, BRL } from "@/lib/api";
import { toast } from "sonner";
import { getSubscription } from "@/lib/push";

const REQUIRED_LABELS = {
  customer_name: "Nome",
  phone: "Telefone / WhatsApp",
  neighborhood_id: "Bairro",
  address: "Endereço de entrega",
  house_number: "Número da casa",
  payment_method: "Forma de pagamento",
};
const FIELD_TESTIDS = {
  customer_name: "checkout-name",
  phone: "checkout-phone",
  neighborhood_id: "checkout-neighborhood",
  address: "checkout-address",
  house_number: "checkout-house-number",
  payment_method: "checkout-payment",
};

const nativeSelectClass =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary transition-colors appearance-none";

export default function CartSheet() {
  const { items, setQty, remove, total, clear, open, setOpen } = useCart();
  const [checkout, setCheckout] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    address: "",
    house_number: "",
    payment_method: "",
    notes: "",
    fulfillment_type: "entrega",
    neighborhood_id: "",
  });
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [cardFeePercent, setCardFeePercent] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [storeOpen, setStoreOpen] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/neighborhoods").then(({ data }) => setNeighborhoods(data)).catch(() => {});
    api.get("/settings").then(({ data }) => {
      setCardFeePercent(data.card_fee_percent || 0);
      setStoreOpen(data.is_open !== false);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (checkout) {
      api.get("/settings").then(({ data }) => setStoreOpen(data.is_open !== false)).catch(() => {});
    }
  }, [checkout]);

  const [missing, setMissing] = useState("");
  const set = (k) => (e) => {
    if (k === missing) setMissing("");
    setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  };
  const errCls = (k) => (missing === k ? "border-red-500 ring-2 ring-red-500/30" : "");
  const ErrMsg = ({ k }) =>
    missing === k ? (
      <p data-testid={`checkout-error-${k}`} className="text-xs font-semibold text-red-600 mt-1">
        {REQUIRED_LABELS[k]} é obrigatório.
      </p>
    ) : null;

  const isDelivery = form.fulfillment_type === "entrega";
  const selectedNb = neighborhoods.find((n) => n.id === form.neighborhood_id);
  const deliveryFee = isDelivery && selectedNb ? selectedNb.fee : 0;
  const isCard = form.payment_method === "Cartão na entrega";
  const cardFee = isCard && cardFeePercent > 0 ? Math.round((total + deliveryFee) * cardFeePercent) / 100 : 0;
  const grandTotal = total + deliveryFee + cardFee;

  const placeOrder = async () => {
    const required = ["customer_name", "phone"];
    if (isDelivery) required.push("neighborhood_id", "address", "house_number");
    required.push("payment_method");
    const first = required.find((k) => !String(form[k] || "").trim());
    if (first) {
      setMissing(first);
      toast.error(`Falta preencher: ${REQUIRED_LABELS[first]}`);
      const el = document.querySelector(`[data-testid="${FIELD_TESTIDS[first]}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => el.focus({ preventScroll: true }), 300);
      }
      return;
    }
    setMissing("");
    setSubmitting(true);
    try {
      const push_subscription = await Promise.race([
        getSubscription(true).catch(() => null),
        new Promise((r) => setTimeout(() => r(null), 6000)),
      ]);
      const { data } = await api.post("/orders", {
        ...form,
        neighborhood_id: isDelivery ? form.neighborhood_id : null,
        items,
        push_subscription,
      });
      clear();
      setCheckout(false);
      setOpen(false);
      toast.success(`Pedido #${data.order_number} enviado!`);
      navigate(`/pedido/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível enviar o pedido.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0" data-testid="cart-sheet">
        <SheetHeader className="p-5 border-b border-border">
          <SheetTitle className="font-display font-black text-2xl tracking-tight text-left">
            {checkout ? "Finalizar pedido" : "Meu pedido"}
          </SheetTitle>
          <SheetDescription className="text-left">
            {checkout ? "Informe seus dados para entrega." : "Revise os itens do seu pedido."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 && (
            <div className="text-center text-muted-foreground py-16">
              <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
              Seu pedido está vazio.
            </div>
          )}

          {!checkout &&
            items.map((it) => (
              <div
                key={it.product_id}
                data-testid={`cart-item-${it.product_id}`}
                className="flex items-center gap-3 bg-muted/40 rounded-xl p-3"
              >
                <div className="flex-1">
                  <p className="font-semibold text-sm">{it.name}</p>
                  <p className="text-sm text-primary font-bold">{BRL(it.price)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setQty(it.product_id, it.qty - 1)}
                    data-testid={`decrease-${it.product_id}`}
                    className="w-7 h-7 grid place-items-center rounded-full border border-border hover:bg-muted"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="w-5 text-center font-semibold">{it.qty}</span>
                  <button
                    onClick={() => setQty(it.product_id, it.qty + 1)}
                    data-testid={`increase-${it.product_id}`}
                    className="w-7 h-7 grid place-items-center rounded-full border border-border hover:bg-muted"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(it.product_id)}
                    data-testid={`remove-${it.product_id}`}
                    className="w-7 h-7 grid place-items-center rounded-full text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}

          {checkout && (
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input
                  data-testid="checkout-name"
                  value={form.customer_name}
                  onChange={set("customer_name")}
                  placeholder="Seu nome"
                  className={errCls("customer_name")}
                />
                <ErrMsg k="customer_name" />
              </div>
              <div>
                <Label>Telefone / WhatsApp</Label>
                <Input
                  data-testid="checkout-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="(00) 00000-0000"
                  className={errCls("phone")}
                />
                <ErrMsg k="phone" />
              </div>
              <div>
                <Label>Como você quer receber?</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, fulfillment_type: "entrega" }))}
                    data-testid="fulfillment-entrega"
                    className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold transition-colors ${
                      isDelivery ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"
                    }`}
                  >
                    <Bike className="w-4 h-4" /> Entrega
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, fulfillment_type: "retirada" }))}
                    data-testid="fulfillment-retirada"
                    className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold transition-colors ${
                      !isDelivery ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"
                    }`}
                  >
                    <Store className="w-4 h-4" /> Retirar no local
                  </button>
                </div>
              </div>
              {isDelivery && (
                <>
                  <div>
                    <Label>Bairro (taxa de entrega)</Label>
                    <select
                      value={form.neighborhood_id}
                      onChange={set("neighborhood_id")}
                      data-testid="checkout-neighborhood"
                      className={`${nativeSelectClass} ${errCls("neighborhood_id")}`}
                    >
                      <option value="">Selecione seu bairro</option>
                      {neighborhoods.map((n) => (
                        <option key={n.id} value={n.id}>
                          {n.name} — {BRL(n.fee)}
                        </option>
                      ))}
                    </select>
                    <ErrMsg k="neighborhood_id" />
                  </div>
                  <div>
                    <Label>Endereço de entrega</Label>
                    <Textarea
                      data-testid="checkout-address"
                      value={form.address}
                      onChange={set("address")}
                      placeholder="Rua, referência"
                      className={errCls("address")}
                    />
                    <ErrMsg k="address" />
                  </div>
                  <div>
                    <Label>Número da casa *</Label>
                    <Input
                      data-testid="checkout-house-number"
                      value={form.house_number}
                      onChange={set("house_number")}
                      placeholder="Ex.: 123 ou S/N"
                      required
                      className={errCls("house_number")}
                    />
                    <ErrMsg k="house_number" />
                  </div>
                </>
              )}
              {!isDelivery && (
                <p className="text-sm text-muted-foreground bg-muted/50 rounded-xl p-3">
                  Retirada no balcão — Rua José Pequeno 50, Alto Alegre. Sem taxa de entrega.
                </p>
              )}
              <div>
                <Label>Forma de pagamento</Label>
                <select
                  value={form.payment_method}
                  onChange={set("payment_method")}
                  data-testid="checkout-payment"
                  className={`${nativeSelectClass} ${errCls("payment_method")}`}
                >
                  <option value="">Selecione</option>
                  <option value="Dinheiro">Dinheiro</option>
                  <option value="Pix">Pix</option>
                  <option value="Cartão na entrega">Cartão na entrega</option>
                </select>
                <ErrMsg k="payment_method" />
              </div>
              <div>
                <Label>Observações (opcional)</Label>
                <Textarea
                  data-testid="checkout-notes"
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Ex.: sem cebola, troco para R$50..."
                />
              </div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border p-5 space-y-3">
            {checkout && (
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold" data-testid="cart-subtotal">{BRL(total)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {isDelivery ? "Taxa de entrega" : "Retirada no local"}
                  </span>
                  <span className="font-semibold" data-testid="cart-delivery-fee">
                    {isDelivery ? (selectedNb ? BRL(deliveryFee) : "—") : "Grátis"}
                  </span>
                </div>
                {isCard && cardFeePercent > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      Taxa do cartão ({cardFeePercent}%)
                    </span>
                    <span className="font-semibold" data-testid="cart-card-fee">
                      {BRL(cardFee)}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total</span>
              <span className="font-display font-black text-2xl text-primary" data-testid="cart-total">
                {BRL(checkout ? grandTotal : total)}
              </span>
            </div>
            {!checkout ? (
              <button
                onClick={() => setCheckout(true)}
                data-testid="go-to-checkout-btn"
                className="w-full rounded-full bg-primary text-primary-foreground py-3 font-semibold hover:bg-primary/90 transition-colors"
              >
                Continuar
              </button>
            ) : (
              <div className="space-y-2">
                {!storeOpen && (
                  <p data-testid="checkout-closed-notice" className="text-sm text-center font-semibold text-red-600">
                    Estamos fechados no momento. Não é possível finalizar pedidos agora.
                  </p>
                )}
                <button
                  onClick={placeOrder}
                  disabled={submitting || !storeOpen}
                  data-testid="place-order-btn"
                  className="w-full rounded-full bg-primary text-primary-foreground py-3 font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {!storeOpen ? "Loja fechada" : submitting ? "Enviando..." : "Enviar pedido"}
                </button>
                <button
                  onClick={() => setCheckout(false)}
                  className="w-full rounded-full border border-border py-2.5 font-medium hover:bg-muted transition-colors"
                >
                  Voltar
                </button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
